/* SSi website – Welsh landing page "Reasons" section.
 *
 * Three-up, identification-style. People come to Welsh for very
 * different reasons; this section names the three biggest groups so
 * the visitor can find themselves. Sits high on the page – between
 * the hero and methodology – so we acknowledge "I see you" before
 * launching into how the method works.
 *
 * Each card has:
 *   – a tiny meta tag (Reclaiming / Belonging / Connecting)
 *   – a one-line title written in second person
 *   – a body paragraph
 *   – a quiet ornamental illustration (cream-on-cream) so the section
 *     stays calm and editorial; the loud illustrations are saved for
 *     the vignettes block further down.
 */

export function WelshReasons({ t }) {
  const cards = [
    {
      tag: t.cyReasonsTag1, title: t.cyReasonsTitle1, body: t.cyReasonsBody1,
      svg: <ReasonInheritance/>,
    },
    {
      tag: t.cyReasonsTag2, title: t.cyReasonsTitle2, body: t.cyReasonsBody2,
      svg: <ReasonHouse/>,
    },
    {
      tag: t.cyReasonsTag3, title: t.cyReasonsTitle3, body: t.cyReasonsBody3,
      svg: <ReasonGlobe/>,
    },
  ];

  return (
    <section className="ssi-cyReasons" style={cyReasonsStyles.wrap}>
      <div style={cyReasonsStyles.inner}>
        <div style={cyReasonsStyles.head}>
          <div style={cyReasonsStyles.kicker}>{t.cyReasonsKicker}</div>
          <h2 style={cyReasonsStyles.h2}>{t.cyReasonsTitle}</h2>
          <p style={cyReasonsStyles.lede}>{t.cyReasonsLede}</p>
        </div>

        <ul style={cyReasonsStyles.grid}>
          {cards.map((c, i) => (
            <li key={i} style={cyReasonsStyles.card}>
              <div style={cyReasonsStyles.illoWrap}>{c.svg}</div>
              <div style={cyReasonsStyles.tag}>{c.tag}</div>
              <h3 style={cyReasonsStyles.cardH}>{c.title}</h3>
              <p style={cyReasonsStyles.cardBody}>{c.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ───────────── quiet, ornamental SVGs ─────────────
 * Single-stroke, deepRed on cream. Read as marginalia rather than
 * illustration; doesn't compete with the brighter vignettes below.
 */

function ReasonInheritance() {
  // Three nested branches – grandparent, parent, you.
  return (
    <svg viewBox="0 0 80 80" width="100%" height="100%" fill="none">
      <line x1="40" y1="14" x2="40" y2="70" stroke="#490300" strokeWidth="1.2"/>
      <circle cx="40" cy="16" r="3" fill="#DB1E17" stroke="#490300" strokeWidth="1.2"/>
      <line x1="40" y1="30" x2="22" y2="38" stroke="#490300" strokeWidth="1.2"/>
      <circle cx="22" cy="38" r="3" fill="#FEC902" stroke="#490300" strokeWidth="1.2"/>
      <line x1="40" y1="46" x2="58" y2="54" stroke="#490300" strokeWidth="1.2"/>
      <circle cx="58" cy="54" r="3" fill="#FEC902" stroke="#490300" strokeWidth="1.2"/>
      <circle cx="40" cy="68" r="4" fill="#DB1E17" stroke="#490300" strokeWidth="1.2"/>
      {/* small leaves */}
      <path d="M22 38 Q18 32 14 36" stroke="#490300" strokeWidth="1" fill="none"/>
      <path d="M58 54 Q62 48 66 52" stroke="#490300" strokeWidth="1" fill="none"/>
    </svg>
  );
}

function ReasonHouse() {
  // Welsh longhouse with smoking chimney – "you live here now".
  return (
    <svg viewBox="0 0 80 80" width="100%" height="100%" fill="none">
      <line x1="6" y1="68" x2="74" y2="68" stroke="#490300" strokeWidth="1.2"/>
      {/* house */}
      <rect x="18" y="40" width="44" height="28" fill="#fff" stroke="#490300" strokeWidth="1.2"/>
      {/* roof */}
      <path d="M14 42 L40 22 L66 42 Z" fill="#DB1E17" stroke="#490300" strokeWidth="1.2"/>
      {/* chimney */}
      <rect x="50" y="22" width="6" height="12" fill="#900600" stroke="#490300" strokeWidth="1.2"/>
      {/* smoke */}
      <path d="M53 18 Q56 14 53 10 Q50 6 53 2" stroke="#490300" strokeWidth="1" fill="none" opacity=".5"/>
      {/* door */}
      <rect x="36" y="52" width="8" height="16" fill="#490300"/>
      {/* window */}
      <rect x="24" y="48" width="8" height="8" fill="#FEC902" stroke="#490300" strokeWidth="1"/>
      <rect x="48" y="48" width="8" height="8" fill="#FEC902" stroke="#490300" strokeWidth="1"/>
      <line x1="28" y1="48" x2="28" y2="56" stroke="#490300" strokeWidth=".8"/>
      <line x1="52" y1="48" x2="52" y2="56" stroke="#490300" strokeWidth=".8"/>
    </svg>
  );
}

function ReasonGlobe() {
  // Globe with a long line connecting Wales-ish dot to a far dot.
  return (
    <svg viewBox="0 0 80 80" width="100%" height="100%" fill="none">
      <circle cx="40" cy="40" r="26" fill="#fff" stroke="#490300" strokeWidth="1.2"/>
      {/* meridians */}
      <ellipse cx="40" cy="40" rx="10" ry="26" stroke="#490300" strokeWidth="1" fill="none"/>
      <line x1="14" y1="40" x2="66" y2="40" stroke="#490300" strokeWidth="1"/>
      {/* Wales dot */}
      <circle cx="34" cy="32" r="3" fill="#DB1E17" stroke="#490300" strokeWidth="1"/>
      {/* Far dot */}
      <circle cx="54" cy="50" r="3" fill="#FEC902" stroke="#490300" strokeWidth="1"/>
      {/* arc of connection */}
      <path d="M34 32 Q44 18 54 50" stroke="#490300" strokeWidth="1.2" fill="none" strokeDasharray="2 3"/>
    </svg>
  );
}

const cyReasonsStyles = {
  wrap: { background: 'var(--cream, #FBF7F0)', padding: '96px 32px' },
  inner: { maxWidth: 1240, margin: '0 auto' },
  head: { maxWidth: 760, marginBottom: 48 },
  kicker: {
    fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--brandRed)', fontWeight: 700, marginBottom: 14,
  },
  h2: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 48, lineHeight: 1.08,
    letterSpacing: '-.015em', margin: '0 0 18px',
  },
  lede: {
    fontFamily: 'var(--font-body)', fontSize: 18, lineHeight: 1.55,
    color: 'var(--grey)', margin: 0, textWrap: 'pretty', maxWidth: '60ch',
  },

  grid: {
    listStyle: 'none', margin: 0, padding: 0,
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28,
  },
  card: {
    background: '#fff',
    border: '1px solid rgba(73,3,0,.12)',
    borderRadius: 18,
    padding: '28px 28px 30px',
    display: 'flex', flexDirection: 'column', gap: 8,
    boxShadow: '0 10px 24px -18px rgba(73,3,0,.35)',
  },
  illoWrap: {
    width: 64, height: 64,
    marginBottom: 6,
  },
  tag: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    fontSize: 11, fontWeight: 700, letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: 'var(--brandRed)',
  },
  cardH: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 22, lineHeight: 1.2,
    margin: '2px 0 6px', color: 'var(--fg-1)',
    letterSpacing: '-.005em',
    textWrap: 'balance',
  },
  cardBody: {
    fontFamily: 'var(--font-body)',
    fontSize: 15, lineHeight: 1.55,
    color: 'var(--fg-1)', margin: 0,
    textWrap: 'pretty',
  },
};
